import React, { useEffect, useState, useCallback } from 'react';
// Chakra shell (consistente con las demás vistas)
import { Flex, Text, Divider, useColorModeValue } from '@chakra-ui/react';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
// MUI para formularios/tabla
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

const CALF_GREEN = '#8ebf63';

function GestionTarifas() {
  const textColor = useColorModeValue('gray.700', 'white');
  const base = `${process.env.REACT_APP_API_URL}/tarifagrupo`;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token'),
  };

  const [grupos, setGrupos] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [nuevoGrupo, setNuevoGrupo] = useState('');
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });

  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const cargarGrupos = useCallback(async () => {
    try {
      const r = await fetch(`${base}/grupos`, { headers });
      setGrupos(await r.json());
    } catch (e) { notify('Error cargando grupos', 'error'); }
  }, []);

  const cargarTarifas = useCallback(async () => {
    try {
      const r = await fetch(`${base}/tarifas`, { headers });
      setTarifas(await r.json());
    } catch (e) { notify('Error cargando tarifas', 'error'); }
  }, []);

  useEffect(() => { cargarGrupos(); cargarTarifas(); }, [cargarGrupos, cargarTarifas]);

  // ─── grupos ───
  const crearGrupo = async () => {
    const nombre = nuevoGrupo.trim();
    if (!nombre) return;
    const r = await fetch(`${base}/grupos`, {
      method: 'POST', headers, body: JSON.stringify({ nombre, orden: grupos.length + 1 }),
    });
    if (r.ok) { setNuevoGrupo(''); notify('Grupo creado'); cargarGrupos(); }
    else { const j = await r.json().catch(() => ({})); notify(j.message || 'No se pudo crear', 'error'); }
  };

  const guardarGrupo = async (g) => {
    const r = await fetch(`${base}/grupos/${g.grupoId}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ nombre: g.nombre, orden: g.orden, activo: g.activo }),
    });
    if (r.ok) { notify('Grupo actualizado'); cargarGrupos(); }
    else { const j = await r.json().catch(() => ({})); notify(j.message || 'No se pudo guardar', 'error'); }
  };

  const borrarGrupo = async (id) => {
    if (!window.confirm('¿Borrar el grupo? Las tarifas asignadas quedarán sin grupo.')) return;
    const r = await fetch(`${base}/grupos/${id}`, { method: 'DELETE', headers });
    if (r.ok) { notify('Grupo borrado'); cargarGrupos(); cargarTarifas(); }
    else notify('No se pudo borrar', 'error');
  };

  const editarGrupoLocal = (id, campo, valor) =>
    setGrupos(prev => prev.map(g => g.grupoId === id ? { ...g, [campo]: valor } : g));

  // ─── tarifas ───
  const asignarGrupo = async (cod, grupoIdRaw) => {
    const grupoId = grupoIdRaw === '' ? null : parseInt(grupoIdRaw, 10);
    const r = await fetch(`${base}/tarifas/${encodeURIComponent(cod)}`, {
      method: 'PUT', headers, body: JSON.stringify({ grupoId }),
    });
    if (r.ok) {
      setTarifas(prev => prev.map(t => t.ctgEttCod === cod
        ? { ...t, grupoId, grupoNombre: grupos.find(g => g.grupoId === grupoId)?.nombre ?? null }
        : t));
    } else notify('No se pudo asignar', 'error');
  };

  const sinGrupo = tarifas.filter(t => t.grupoId == null).length;

  return (
    <Flex direction="column" pt={{ base: '120px', md: '120px' }}>
      {/* GRUPOS */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }} pb="0px" mb="24px">
        <CardHeader p="6px 0px 22px 0px">
          <Text fontSize="xl" color={textColor} fontWeight="bold">Grupos macro</Text>
          <Divider />
        </CardHeader>
        <CardBody>
          <Box sx={{ width: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Orden</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grupos.map(g => (
                  <TableRow key={g.grupoId}>
                    <TableCell>
                      <TextField size="small" fullWidth value={g.nombre}
                        onChange={e => editarGrupoLocal(g.grupoId, 'nombre', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={g.orden}
                        onChange={e => editarGrupoLocal(g.grupoId, 'orden', parseInt(e.target.value, 10) || 0)} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" size="small" onClick={() => guardarGrupo(g)} title="Guardar">
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => borrarGrupo(g.grupoId)} title="Borrar">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField size="small" placeholder="Nuevo grupo…" value={nuevoGrupo}
                        onChange={e => setNuevoGrupo(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') crearGrupo(); }} />
                      <Button variant="contained" startIcon={<AddIcon />} onClick={crearGrupo}
                        sx={{ bgcolor: CALF_GREEN, '&:hover': { bgcolor: '#7aad50' } }}>
                        Agregar
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      {/* TARIFAS */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }} pb="0px">
        <CardHeader p="6px 0px 22px 0px">
          <Flex justify="space-between" align="center" w="100%">
            <Text fontSize="xl" color={textColor} fontWeight="bold">Clasificación de tarifas</Text>
            {sinGrupo > 0 && <Chip color="warning" size="small" label={`${sinGrupo} sin grupo`} />}
          </Flex>
          <Divider />
        </CardHeader>
        <CardBody>
          <Box sx={{ width: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tarifa (CtgEttCod)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Grupo macro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tarifas.map(t => (
                  <TableRow key={t.ctgEttCod}
                    sx={{ bgcolor: t.grupoId == null ? '#fff8e1' : 'inherit' }}>
                    <TableCell>{t.ctgEttCod}</TableCell>
                    <TableCell>
                      <TextField select size="small" sx={{ minWidth: 220 }}
                        value={t.grupoId ?? ''}
                        onChange={e => asignarGrupo(t.ctgEttCod, e.target.value)}>
                        <MenuItem value=""><em>— Sin grupo —</em></MenuItem>
                        {grupos.map(g => <MenuItem key={g.grupoId} value={g.grupoId}>{g.nombre}</MenuItem>)}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      <Snackbar open={toast.open} autoHideDuration={3000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Flex>
  );
}

export default GestionTarifas;
